// Owner portfolio edit: list, photos, save, visibility
import { apiFetchJSON } from '../../core/api.js';
import { buildPresetSpecs, escapeAttr } from '../../core/format.js';
import { compressImageFile } from '../../core/images.js';
import { getTg } from '../../core/telegram.js';
import { getCurrentUser } from '../../app/app-state.js';
import { getInput, OWNER_LIST_LOADING_HTML } from '../../core/dom.js';
import { fetchPortfolio } from './list.js';
import type { ApiResponse, PortfolioItem } from '../../types/api.js';

function portfolioKindLabel(kind: string): string {
  if (kind === 'catalog') return 'Готовая';
  if (kind === 'upgrade') return 'Апгрейд';
  return 'На заказ';
}

const editPortfolioImages: Record<string, string[]> = {};
let replacePortfolioEditIndex: number | null = null;
let replacePortfolioEditId: string | null = null;

function renderOwnerPortfolioEditCard(item: PortfolioItem): string {
  const pid = String(item.id);
  const kind = String(item.kind || 'custom');
  const specs = buildPresetSpecs(item.specs || item);
  const review = item.review && item.review.text ? item.review : null;
  const imgs = editPortfolioImages[pid] || item.images || (item.image ? [item.image] : []);
  const inputId = 'portfolio-edit-image-' + escapeAttr(item.id);
  let imgBlock = '<div class="photo-gallery preset-edit-gallery">';
  imgBlock += '<div class="photo-preview-list preset-edit-preview-list" data-portfolio-id="' + escapeAttr(item.id) + '">';
  for (let idx = 0; idx < imgs.length; idx++) {
    imgBlock += '<div class="photo-preview-card" data-photo-index="' + idx + '">';
    imgBlock += '<img src="' + escapeAttr(imgs[idx]) + '" alt="" />';
    if (idx === 0) imgBlock += '<span class="photo-preview-badge">Главная</span>';
    imgBlock +=
      '<button type="button" class="photo-preview-edit portfolio-edit-photo-replace" data-portfolio-id="' +
      escapeAttr(item.id) +
      '" data-photo-index="' +
      idx +
      '" aria-label="Изменить">';
    imgBlock +=
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
    imgBlock += '</button></div>';
  }
  imgBlock += '</div>';
  imgBlock += '<label class="photo-add-card preset-edit-photo-card photo-add-inline" for="' + inputId + '">';
  imgBlock +=
    '<input type="file" class="photo-add-input portfolio-edit-image-input" id="' +
    inputId +
    '" accept="image/*" data-portfolio-id="' +
    escapeAttr(item.id) +
    '" />';
  imgBlock += '<span class="photo-add-icon"></span>';
  imgBlock += '<span class="photo-add-text">Добавить фото</span>';
  imgBlock += '</label></div>';

  let html = '<article class="preset-edit-card" data-portfolio-id="' + escapeAttr(item.id) + '">';
  html +=
    '<div class="preset-visibility-row"><span class="preset-visibility-badge' +
    (item.hidden ? ' preset-visibility-badge-hidden' : ' preset-visibility-badge-visible') +
    '">' +
    (item.hidden ? 'Скрыто у пользователей' : 'Показывается пользователям') +
    '</span></div>';
  html += '<label class="form-label">Тип работы</label>';
  html += '<div class="portfolio-edit-kind-row" data-portfolio-id="' + escapeAttr(item.id) + '">';
  (['custom', 'catalog', 'upgrade'] as const).forEach(function (k) {
    html +=
      '<button type="button" class="portfolio-kind-chip portfolio-edit-kind' +
      (kind === k ? ' is-active' : '') +
      '" data-portfolio-id="' +
      escapeAttr(item.id) +
      '" data-kind="' +
      k +
      '">' +
      escapeAttr(portfolioKindLabel(k)) +
      '</button>';
  });
  html += '</div>';
  html += '<label class="form-label">Название</label>';
  html += '<input class="form-input portfolio-edit-title" value="' + escapeAttr(item.title) + '" data-portfolio-id="' + escapeAttr(item.id) + '" />';
  html += '<label class="form-label">Бюджет, ₽</label>';
  html +=
    '<input class="form-input portfolio-edit-budget" value="' +
    escapeAttr(item.budgetRub != null ? String(item.budgetRub) : '') +
    '" data-portfolio-id="' +
    escapeAttr(item.id) +
    '" placeholder="Необязательно" />';
  html += '<label class="form-label">Процессор</label>';
  html += '<input class="form-input portfolio-edit-cpu" value="' + escapeAttr(specs.cpu) + '" data-portfolio-id="' + escapeAttr(item.id) + '" />';
  html += '<label class="form-label">Видеокарта</label>';
  html += '<input class="form-input portfolio-edit-gpu" value="' + escapeAttr(specs.gpu) + '" data-portfolio-id="' + escapeAttr(item.id) + '" />';
  html += '<label class="form-label">ОЗУ</label>';
  html += '<input class="form-input portfolio-edit-ram" value="' + escapeAttr(specs.ram) + '" data-portfolio-id="' + escapeAttr(item.id) + '" />';
  html += '<label class="form-label">Накопители</label>';
  html += '<input class="form-input portfolio-edit-storage" value="' + escapeAttr(specs.storage) + '" data-portfolio-id="' + escapeAttr(item.id) + '" />';
  html += '<label class="form-label">Корпус</label>';
  html += '<input class="form-input portfolio-edit-case" value="' + escapeAttr(specs.caseName) + '" data-portfolio-id="' + escapeAttr(item.id) + '" />';
  html += '<h3 class="photo-section-title preset-edit-photo-title">Фотографии</h3>';
  html += '<div class="preset-edit-image-wrap">' + imgBlock + '</div>';
  html += '<label class="form-label">Описание</label>';
  html +=
    '<textarea class="form-textarea form-textarea-sm portfolio-edit-desc" data-portfolio-id="' +
    escapeAttr(item.id) +
    '">' +
    escapeAttr(item.description) +
    '</textarea>';
  html += '<div class="portfolio-edit-review-block">';
  html += '<label class="form-label">Отзыв клиента (необязательно)</label>';
  html +=
    '<input class="form-input portfolio-edit-review-author" value="' +
    escapeAttr(review ? review.author : '') +
    '" data-portfolio-id="' +
    escapeAttr(item.id) +
    '" placeholder="Имя" />';
  html += '<label class="form-label">Оценка 1–5</label>';
  html +=
    '<input class="form-input portfolio-edit-review-rating" type="number" min="1" max="5" value="' +
    escapeAttr(review ? String(review.rating || 5) : '5') +
    '" data-portfolio-id="' +
    escapeAttr(item.id) +
    '" />';
  html +=
    '<textarea class="form-textarea form-textarea-sm portfolio-edit-review-text" data-portfolio-id="' +
    escapeAttr(item.id) +
    '" placeholder="Текст отзыва">' +
    escapeAttr(review ? review.text : '') +
    '</textarea>';
  html += '</div>';
  html += '<div class="preset-edit-actions">';
  html +=
    '<button type="button" class="submit-btn preset-save-btn portfolio-save-btn" data-portfolio-id="' +
    escapeAttr(item.id) +
    '">Сохранить изменения</button>';
  html +=
    '<button type="button" class="preset-visibility-toggle-btn portfolio-visibility-btn" data-portfolio-id="' +
    escapeAttr(item.id) +
    '" data-hidden="' +
    (item.hidden ? '1' : '0') +
    '">' +
    (item.hidden ? 'Показать пользователям' : 'Скрыть у пользователей') +
    '</button>';
  html +=
    '<button type="button" class="preset-delete-btn portfolio-delete-btn" data-portfolio-id="' +
    escapeAttr(item.id) +
    '">Удалить</button>';
  html += '</div></article>';
  return html;
}

export async function loadOwnerEditPortfolio(): Promise<void> {
  const listEl = document.getElementById('owner-portfolio-list');
  if (!listEl) return;
  listEl.innerHTML = OWNER_LIST_LOADING_HTML;
  const items = await fetchPortfolio({ includeHidden: true });
  if (!items.length) {
    listEl.innerHTML = '<p class="consult-text">Нет работ. Создайте новую в разделе «Создать новую работу».</p>';
    return;
  }
  Object.keys(editPortfolioImages).forEach((k) => delete editPortfolioImages[k]);
  items.forEach(function (it) {
    editPortfolioImages[String(it.id)] = it.images || (it.image ? [it.image] : []);
  });
  listEl.innerHTML = items.map(renderOwnerPortfolioEditCard).join('');

  listEl.querySelectorAll('.portfolio-edit-kind').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      const pid = btn.getAttribute('data-portfolio-id');
      const card = listEl.querySelector('.preset-edit-card[data-portfolio-id="' + pid + '"]');
      if (!card) return;
      card.querySelectorAll('.portfolio-edit-kind').forEach(function (b) {
        b.classList.toggle('is-active', b === btn);
      });
    });
  });

  listEl.querySelectorAll('.portfolio-edit-image-input').forEach(function (inputEl) {
    const input = inputEl as HTMLInputElement;
    input.addEventListener('change', function () {
      const pid = input.getAttribute('data-portfolio-id');
      if (!pid || !input.files || !input.files[0]) return;
      const file = input.files[0];
      compressImageFile(file, 1280, 0.8).then(function (dataUrl: string) {
        const imgs = editPortfolioImages[pid] || [];
        const idx = replacePortfolioEditIndex;
        const replacePid = replacePortfolioEditId;
        replacePortfolioEditIndex = null;
        replacePortfolioEditId = null;

        if (typeof idx === 'number' && idx >= 0 && replacePid === pid && imgs[idx] !== undefined) {
          imgs[idx] = dataUrl;
          const card = listEl.querySelector(
            '.preset-edit-preview-list[data-portfolio-id="' + pid.replace(/"/g, '\\"') + '"] .photo-preview-card[data-photo-index="' + idx + '"]'
          );
          if (card) {
            const imgEl = card.querySelector('img') as HTMLImageElement | null;
            if (imgEl) imgEl.src = dataUrl;
          }
        } else {
          imgs.push(dataUrl);
          const previewList = listEl.querySelector(
            '.preset-edit-preview-list[data-portfolio-id="' + pid.replace(/"/g, '\\"') + '"]'
          );
          if (previewList) {
            const newIdx = imgs.length - 1;
            const div = document.createElement('div');
            div.className = 'photo-preview-card';
            div.setAttribute('data-photo-index', String(newIdx));
            const img = document.createElement('img');
            img.src = dataUrl;
            img.alt = '';
            div.appendChild(img);
            if (newIdx === 0) {
              const badge = document.createElement('span');
              badge.className = 'photo-preview-badge';
              badge.textContent = 'Главная';
              div.appendChild(badge);
            }
            const editBtn = document.createElement('button');
            editBtn.type = 'button';
            editBtn.className = 'photo-preview-edit portfolio-edit-photo-replace';
            editBtn.setAttribute('data-portfolio-id', pid);
            editBtn.setAttribute('data-photo-index', String(newIdx));
            editBtn.setAttribute('aria-label', 'Изменить');
            editBtn.innerHTML =
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
            div.appendChild(editBtn);
            previewList.appendChild(div);
            editBtn.addEventListener('click', function (ev) {
              ev.preventDefault();
              ev.stopPropagation();
              replacePortfolioEditIndex = parseInt(editBtn.getAttribute('data-photo-index') || '0', 10);
              replacePortfolioEditId = editBtn.getAttribute('data-portfolio-id');
              input.click();
            });
          }
        }
        editPortfolioImages[pid] = imgs;
      });
      input.value = '';
    });
  });

  listEl.querySelectorAll('.portfolio-edit-photo-replace').forEach(function (btnEl) {
    const btn = btnEl as HTMLButtonElement;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      const pid = btn.getAttribute('data-portfolio-id');
      replacePortfolioEditIndex = parseInt(btn.getAttribute('data-photo-index') || '0', 10);
      replacePortfolioEditId = pid;
      if (pid) getInput('portfolio-edit-image-' + pid)?.click();
    });
  });

  listEl.querySelectorAll('.portfolio-save-btn').forEach(function (btnEl) {
    const btn = btnEl as HTMLButtonElement;
    btn.addEventListener('click', async function (e) {
      e.stopPropagation();
      const t = getTg();
      if (!t || !getCurrentUser()) return;
      const id = btn.getAttribute('data-portfolio-id');
      if (!id) return;
      const card = listEl.querySelector('.preset-edit-card[data-portfolio-id="' + id + '"]');
      if (!card) return;
      const titleEl = card.querySelector('.portfolio-edit-title') as HTMLInputElement | null;
      const descEl = card.querySelector('.portfolio-edit-desc') as HTMLTextAreaElement | null;
      const title = (titleEl?.value || '').trim();
      const description = (descEl?.value || '').trim();
      if (!title) {
        t.showPopup?.({ title: 'Ошибка', message: 'Заполните название.', buttons: [{ type: 'ok' }] });
        return;
      }
      const kindBtn = card.querySelector('.portfolio-edit-kind.is-active');
      const kind = kindBtn ? kindBtn.getAttribute('data-kind') : 'custom';
      const reviewText = ((card.querySelector('.portfolio-edit-review-text') as HTMLTextAreaElement | null)?.value || '').trim();
      let review: { author: string; rating: number; text: string; source: string } | null = null;
      if (reviewText) {
        review = {
          author:
            ((card.querySelector('.portfolio-edit-review-author') as HTMLInputElement | null)?.value || '').trim() ||
            'Покупатель',
          rating: Math.max(
            1,
            Math.min(
              5,
              parseInt((card.querySelector('.portfolio-edit-review-rating') as HTMLInputElement | null)?.value || '5', 10) || 5
            )
          ),
          text: reviewText,
          source: 'manual'
        };
      }
      const imgs = editPortfolioImages[id] || [];
      const payload = {
        id: id,
        kind: kind,
        title: title,
        description: description,
        images: imgs,
        budgetRub: ((card.querySelector('.portfolio-edit-budget') as HTMLInputElement | null)?.value || '').trim(),
        specs: {
          cpu: ((card.querySelector('.portfolio-edit-cpu') as HTMLInputElement | null)?.value || '').trim(),
          gpu: ((card.querySelector('.portfolio-edit-gpu') as HTMLInputElement | null)?.value || '').trim(),
          ram: ((card.querySelector('.portfolio-edit-ram') as HTMLInputElement | null)?.value || '').trim(),
          storage: ((card.querySelector('.portfolio-edit-storage') as HTMLInputElement | null)?.value || '').trim(),
          caseName: ((card.querySelector('.portfolio-edit-case') as HTMLInputElement | null)?.value || '').trim()
        },
        review: review
      };
      btn.disabled = true;
      btn.textContent = 'Сохранение...';
      try {
        const data = await apiFetchJSON<ApiResponse | null>('/api/portfolio/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).catch(function () {
          return null;
        });
        if (data && data.ok) {
          await loadOwnerEditPortfolio();
          t.showPopup?.({ title: 'Готово', message: 'Изменения сохранены.', buttons: [{ type: 'ok' }] });
        } else {
          t.showPopup?.({ title: 'Ошибка', message: 'Не удалось сохранить.', buttons: [{ type: 'ok' }] });
        }
      } catch (_) {
        t.showPopup?.({ title: 'Ошибка', message: 'Не удалось сохранить.', buttons: [{ type: 'ok' }] });
      } finally {
        btn.disabled = false;
        btn.textContent = 'Сохранить изменения';
      }
    });
  });

  listEl.querySelectorAll('.portfolio-visibility-btn').forEach(function (btnEl) {
    const btn = btnEl as HTMLButtonElement;
    btn.addEventListener('click', async function (e) {
      e.stopPropagation();
      const t = getTg();
      if (!t || !getCurrentUser()) return;
      const id = btn.getAttribute('data-portfolio-id');
      const hidden = btn.getAttribute('data-hidden') === '1';
      try {
        const data = await apiFetchJSON<ApiResponse | null>('/api/portfolio/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: id, hidden: !hidden })
        }).catch(function () {
          return null;
        });
        if (data && data.ok) await loadOwnerEditPortfolio();
      } catch (_) {}
    });
  });

  listEl.querySelectorAll('.portfolio-delete-btn').forEach(function (btnEl) {
    const btn = btnEl as HTMLButtonElement;
    btn.addEventListener('click', async function (e) {
      e.stopPropagation();
      const t = getTg();
      if (!t || !getCurrentUser()) return;
      if (!confirm('Вы точно хотите удалить работу?')) return;
      const id = btn.getAttribute('data-portfolio-id');
      try {
        const data = await apiFetchJSON<ApiResponse | null>('/api/portfolio/delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: id })
        }).catch(function () {
          return null;
        });
        if (data && data.ok) {
          await loadOwnerEditPortfolio();
        } else {
          t.showPopup?.({ title: 'Ошибка', message: 'Не удалось удалить работу.', buttons: [{ type: 'ok' }] });
        }
      } catch (_) {
        t.showPopup?.({ title: 'Ошибка', message: 'Не удалось удалить работу.', buttons: [{ type: 'ok' }] });
      }
    });
  });
}
